import { Box } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSubAssemblies } from "../hooks/useSubAssemblies";
import { useSubAssemblyFilter } from "../hooks/useSubAssemblyFilter";
import { useSubAssemblyForm } from "../hooks/useSubAssemblyForm";
import { SubAssemblyFilterPane } from "./SubAssemblyFilterPane";
import { SubAssemblyGrid } from "./SubAssemblyGrid";
import { SubAssemblyFormDialog } from "../dialogs/SubAssemblyFormDialog";
import { ConfirmDialog } from "../../../common/confirm-dialog";
import { MiniPackSubAssemblyDrawer } from "../dialogs/MiniPackSubAssemblyDrawer";
import { SubAssembly } from "../../../../types/subAssembly";

const SUB_ASSEMBLY_PREFIXES: Record<string, string> = {
  "SHOCK STRUT": "ShockStrut",
  "SIDE STRUT": "SideStrut",
  "2ND SIDE STRUT": "SecondSideStrut",
  "DRAG BRACE": "DragBrace",
  "AUX DRAG": "AuxDrag",
};

const normalizeSerial = (value: string): string => {
  const normalized = String(value || "").trim().toUpperCase();
  if (!normalized) {
    return "";
  }

  return normalized.startsWith("SN") ? normalized : `SN${normalized}`;
};

const toPascalCase = (value: string): string =>
  String(value || "")
    .split(/[\s\-_]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join("");

const buildSubAssemblyScopeLabel = (subAssemblyType: string, serialNumber: string): string => {
  const key = String(subAssemblyType || "").trim().toUpperCase();
  const prefix = SUB_ASSEMBLY_PREFIXES[key] || toPascalCase(subAssemblyType);
  return `${prefix}.${normalizeSerial(serialNumber)}`;
};

export function ManageSubAssemblies() {
  const navigate = useNavigate();
  const data = useSubAssemblies();
  const filter = useSubAssemblyFilter(data.subAssemblies);
  const form = useSubAssemblyForm();
  const [miniPackSubAssembly, setMiniPackSubAssembly] = useState<SubAssembly | null>(null);

  const handleEdit = useCallback(
    async (subAssembly: { id: string }) => {
      try {
        const fullItem = await data.fetchById(subAssembly.id);
        if (fullItem) {
          form.edit(fullItem);
        }
      } catch {
        // Error handled in fetchById
      }
    },
    [data, form]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ display: "flex", height: "100%", gap: 2, p: 2 }}>
        <SubAssemblyFilterPane
          filter={filter.filter}
          onChange={filter.setFilter}
          onCreate={form.create}
        />

        <SubAssemblyGrid
          loading={data.loading}
          subAssemblies={filter.filteredSubAssemblies}
          onEdit={handleEdit}
          onDelete={data.remove}
          onMiniPack={setMiniPackSubAssembly}
        />

        <SubAssemblyFormDialog
          open={form.open}
          form={form.form}
          isCreate={form.isCreate}
          change={form.change}
          close={form.close}
          onSave={async (values) => {
            await data.save(values, form.selected?.id);
            form.close();
          }}
        />

        <ConfirmDialog
          open={data.confirmDelete.open}
          title="Delete Sub-Assembly"
          message="Are you sure you want to delete this sub-assembly?"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          isDangerous
          isLoading={data.isDeleting}
          onConfirm={data.handleConfirmDelete}
          onCancel={() => data.setConfirmDelete({ open: false, subAssembly: null })}
        />

        <MiniPackSubAssemblyDrawer
          open={Boolean(miniPackSubAssembly)}
          subAssembly={miniPackSubAssembly}
          onClose={() => setMiniPackSubAssembly(null)}
          onOpenJobs={(subAssembly) => {
            const label = buildSubAssemblyScopeLabel(
              subAssembly.subAssemblyType || "Sub-Assembly",
              subAssembly.serialNumber || ""
            );
            navigate(`/assets/mini-pack-generator?scope=subAssembly&label=${encodeURIComponent(label)}`);
          }}
        />
      </Box>
    </LocalizationProvider>
  );
}
